// oidc-client-ts implementation of AuthBackend (design D4).
import { UserManager, WebStorageStateStore, type User } from "oidc-client-ts"

import type { AppConfig } from "../config"
import { OidcRedirectError, type AuthBackend, type Session } from "./session"

const CALLBACK_PATH = "/auth/callback"

/** sessionStorage wrapper that never persists the access token (it stays in memory only). */
class AccessTokenFreeStorage implements Storage {
  constructor(private readonly inner: Storage) {}
  get length(): number {
    return this.inner.length
  }
  clear(): void {
    this.inner.clear()
  }
  key(index: number): string | null {
    return this.inner.key(index)
  }
  getItem(key: string): string | null {
    return this.inner.getItem(key)
  }
  removeItem(key: string): void {
    this.inner.removeItem(key)
  }
  setItem(key: string, value: string): void {
    if (key.startsWith("oidc.user:")) {
      const user = JSON.parse(value) as Record<string, unknown>
      delete user.access_token
      value = JSON.stringify(user)
    }
    this.inner.setItem(key, value)
  }
}

function toSession(user: User): Session {
  return {
    accessToken: user.access_token,
    expiresAt: user.expires_at ?? 0,
    subject: user.profile.sub,
    email: typeof user.profile.email === "string" ? user.profile.email : undefined,
  }
}

export function oidcBackend(config: AppConfig, origin = window.location.origin): AuthBackend {
  const manager = new UserManager({
    authority: config.oidcIssuer,
    client_id: config.oidcClientId,
    redirect_uri: origin + CALLBACK_PATH,
    post_logout_redirect_uri: origin + "/",
    response_type: "code",
    scope: "openid profile email offline_access",
    userStore: new WebStorageStateStore({ store: new AccessTokenFreeStorage(window.sessionStorage) }),
    automaticSilentRenew: false,
    monitorSession: false,
    loadUserInfo: false,
  })
  return {
    async completeRedirect(url) {
      const parsed = new URL(url)
      if (parsed.pathname !== CALLBACK_PATH) return null
      const error = parsed.searchParams.get("error")
      if (error) throw new OidcRedirectError(error)
      const user = await manager.signinRedirectCallback(url)
      window.history.replaceState(null, "", "/")
      return toSession(user)
    },
    async hasStoredSession() {
      const user = await manager.getUser()
      return Boolean(user?.refresh_token)
    },
    async refresh() {
      const user = await manager.signinSilent()
      if (!user) throw new Error("refresh failed")
      return toSession(user)
    },
    async redirectToSignIn({ prompt }) {
      await manager.signinRedirect(prompt ? { prompt } : undefined)
    },
    async endProviderSession() {
      await manager.signoutRedirect()
    },
    async removeLocal() {
      await manager.removeUser()
    },
  }
}
